import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/services/auth.service";
import {
  GoogleSignInErrorState,
  takeGoogleSignInResume,
} from "@/lib/googleSignIn";

const SIGNUP_PATH = "/affiliate/auth/signup";
const LOGIN_PATH = "/auth/login";

/**
 * Where the backend sends the browser after Google sign-in
 * (`/affiliate/auth/google/complete?ticket=…` or `?error=…`).
 *
 * Redeems the ticket and finishes what the Login / SignUp page would have
 * done in its popup callback: a complete account signs in and goes to its
 * destination; anyone else lands on the signup form with their Google
 * details pre-filled. Errors go back to the page the user started from.
 */
export function GoogleComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  // React 18 StrictMode runs effects twice in development; the ticket is
  // single-use, so the second run must not redeem it again.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(location.search);
    const ticket = params.get("ticket");
    const error = params.get("error");
    const resume = takeGoogleSignInResume();
    const from = resume?.from;

    const fail = (message: string) => {
      const state: GoogleSignInErrorState = { googleError: message };
      navigate(from || LOGIN_PATH, { replace: true, state });
    };

    if (error) {
      fail(
        error === "cancelled"
          ? "Google sign-in was cancelled."
          : error
      );
      return;
    }
    if (!ticket) {
      fail("Google sign-in did not complete. Please try again.");
      return;
    }

    authService
      .redeemGoogleSignInTicket(ticket)
      .then((response) => {
        if (response.needsOnboarding) {
          const googleData = {
            googleId: response.googleId,
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            profileImage: response.profileImage,
          };
          // Keep the signup page's own query (join token, brand, returnUrl)
          // when that is where the user came from.
          const target = from?.startsWith(SIGNUP_PATH) ? from : SIGNUP_PATH;
          navigate(target, { replace: true, state: { googleData } });
          return;
        }

        login({
          user: {
            id: response.id!,
            firstName: response.firstName,
            lastName: response.lastName,
            email: response.email,
            role: "AFFILIATE" as const,
            isActive: true,
            createdAt: new Date().toISOString(),
          },
          token: response.accessToken!,
        });
        navigate(resume?.returnUrl || "/affiliate/dashboard", { replace: true });
      })
      .catch((err: AxiosError<{ message?: string }>) => {
        fail(err.response?.data?.message || "Failed to sign in with Google");
      });
  }, [location.search, login, navigate]);

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-muted-foreground">
      <Spinner label="Signing you in" />
      <p className="text-sm">Finishing sign-in with Google…</p>
    </div>
  );
}
